import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { installAddons, installAreas } from '../../content/products';
import { fmt, incFromEx, toCents } from '../../lib/shopPricing';

// What an install visit shares, once at least one line in the cart is ticked for fitting
// (Hugh, 22 Sep 2026): the customer's tick that the door is inside the Auckland service area
// (with the list one click away), a note, the hub set-up for a smart lock,
// and the price box — as few words as will do (Hugh, 22 Sep 2026: every word fights for its
// place). No suburb, no address and no door question here: the one fitted price
// covers the whole service area, the address is taken when the job is booked, and the fit is
// checked off the door photos after payment — nothing here mentions photos (Hugh, 17 Sep:
// pay first, clarify after). `value` = { inArea, doorNotes, hubSetup };
// `quote` is the cart quote, whose install.tiers rows fill the price box; `smart` says a ticked
// lock has an app ecosystem; `extras` counts the lines that are not fitted and so come with
// the installer; `nudge` outlines the tick when Pay was pressed without it.
export default function InstallPicker({ value, onChange, quote = null, smart = false, extras = 0, nudge = false }) {
  const v = value || {};
  const set = (patch) => onChange({ ...v, ...patch });
  const hubSetup = incFromEx(toCents(installAddons.hubSetup.exGst));
  const inst = quote && !quote.error ? quote.install : null;
  const hasOptional = !!v.doorNotes;

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <label className={`flex items-start gap-3 rounded-lg border-2 p-3 mb-3 cursor-pointer transition-colors ${nudge && !v.inArea ? 'border-red-500 bg-red-50/40' : v.inArea ? 'border-gray-200' : 'border-blue-300 bg-blue-50/40'}`} data-testid="install-in-area">
        <input type="checkbox" checked={!!v.inArea} onChange={(e) => set({ inArea: e.target.checked })} className="mt-1 h-4 w-4" data-testid="install-in-area-toggle" />
        <span className="text-sm">
          <span className="font-semibold text-gray-900">The door is in our service area</span>
          <span className="block text-gray-600">
            {installAreas.summary}.{' '}
            <Link to="/shop/installation#where-we-install" className="text-red-600 font-semibold inline-flex items-center gap-1" target="_blank" rel="noreferrer">See the list <ArrowRight size={12} /></Link>
          </span>
        </span>
      </label>
      {nudge && !v.inArea && (
        <p className="text-sm font-semibold text-red-700 -mt-1 mb-3" role="alert" data-testid="install-in-area-nudge">Tick that the door is in our service area.</p>
      )}
      {/* One note box (Hugh, 22 Sep 2026): times, dogs, gate codes — whatever they want to say. */}
      <details className="mb-3" open={hasOptional || undefined}>
        <summary className="cursor-pointer text-sm text-gray-600 select-none">Add a note with your purchase <span className="text-gray-400">(optional)</span></summary>
        <textarea
          id="install-notes"
          value={v.doorNotes || ''}
          onChange={(e) => set({ doorNotes: e.target.value.slice(0, 300) })}
          rows={2}
          maxLength={300}
          aria-label="Note with your purchase"
          autoComplete="off"
          className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-gray-800 bg-white text-sm"
          data-testid="install-notes"
        />
      </details>
      {smart && (
        <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-3 mb-3 cursor-pointer" data-testid="install-hub-setup">
          <input type="checkbox" checked={!!v.hubSetup} onChange={(e) => set({ hubSetup: e.target.checked })} className="mt-0.5 h-4 w-4" />
          <span className="text-sm">
            <span className="font-semibold text-gray-900">{installAddons.hubSetup.label} · {fmt(hubSetup)}</span>
            <span className="block text-gray-600">Hub plugged in, phone paired, Apple Home / Google / Alexa and first codes set up.</span>
          </span>
        </label>
      )}
      <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3" data-testid="install-price">
        {inst && inst.tiers.map((t) => (
          <p key={t.tier} className="font-bold text-gray-900">
            {t.tierLabel} · {fmt(t.unitCents)} per door{t.count > 1 ? ` × ${t.count} = ${fmt(t.cents)}` : ''}
          </p>
        ))}
        {inst && v.hubSetup && smart && <p className="text-sm text-gray-800 mt-1">+ hub setup {fmt(inst.hubSetupCents)}</p>}
        <p className="text-xs text-gray-600 mt-1">Cover plates included.{extras > 0 ? ' Everything else on the order comes with the installer.' : ''}</p>
      </div>
    </div>
  );
}
